import { useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { io, Socket } from 'socket.io-client';
import { useBackendHealth } from './useBackendHealth';
import { socketBaseUrl, socketIoOptions } from '../utils/socketBaseUrl';

export const DASHBOARD_POLL_MS = 10_000;

const LIVE_REFRESH_EVENTS = [
  'rides:update',
  'orders:update',
  'orders/update',
  'dispatch:queue:update',
  'dispatch:assignment',
  'analytics:live',
] as const;

/** Poll + socket invalidation so dashboard live job counts stay current. */
export function useDashboardLiveRefresh(enabled = true) {
  const queryClient = useQueryClient();
  const { isBackendUp } = useBackendHealth();
  const socketRef = useRef<Socket | null>(null);
  const debounceRef = useRef<number | null>(null);

  const invalidateDashboard = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    queryClient.invalidateQueries({ queryKey: ['god-view'] });
    queryClient.invalidateQueries({ queryKey: ['analytics'] });
    queryClient.invalidateQueries({ queryKey: ['dashboard-reports'] });
  }, [queryClient]);

  const scheduleInvalidate = useCallback(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      debounceRef.current = null;
      invalidateDashboard();
    }, 400);
  }, [invalidateDashboard]);

  useEffect(() => {
    if (!enabled || !isBackendUp) {
      socketRef.current?.disconnect();
      socketRef.current = null;
      return;
    }

    const token = localStorage.getItem('accessToken');
    if (!token) return;
    if (socketRef.current?.connected) return;

    const socket = io(socketBaseUrl(), {
      auth: { token },
      ...socketIoOptions(),
    });

    socket.on('connect', () => {
      socket.emit('dispatch:subscribe');
    });
    socket.io.on('reconnect', () => {
      socket.emit('dispatch:subscribe');
    });

    LIVE_REFRESH_EVENTS.forEach((event) => {
      socket.on(event, scheduleInvalidate);
    });

    socketRef.current = socket;

    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
      socket.disconnect();
      socketRef.current = null;
    };
  }, [enabled, isBackendUp, scheduleInvalidate]);

  return { invalidateDashboard };
}
